/**
 * AI-powered summarization pipeline.
 * Runs after indexing to populate the symbols.summary column using the fast inference model.
 * Uses CachedInferenceService to avoid redundant LLM calls across re-indexes.
 */

import fs from 'node:fs';
import path from 'node:path';
import type { Store } from '../db/store.js';
import { logger } from '../logger.js';
import type { ProgressState } from '../progress.js';
import type { InferenceService, VectorStore } from './interfaces.js';
import { PROMPTS, sanitizeGeneratedSummary, stripLeadingDocstrings } from './prompts.js';

interface SummarizationConfig {
  batchSize: number;
  kinds: string[];
  /** Max parallel inference requests (default 1 = sequential). */
  concurrency: number;
  /**
   * Output-token budget per summary. Overrides
   * `PROMPTS.summarize_symbol.maxTokens` (default 100); wired from
   * `ai.summarize_max_tokens` so reasoning models that burn the budget on
   * thinking tokens can be given headroom.
   */
  maxTokens?: number;
  /**
   * When false, leading docstrings / comment blocks are stripped from the
   * source before it is sent to the summarizer (IPI hardening — docstrings are
   * the highest-risk injection surface). Defaults to true, preserving the
   * original behavior of summarizing from full source including docstrings.
   */
  summarizeFromDocstrings?: boolean;
}

const MAX_SOURCE_LINES = 80;

/**
 * How many times a batch whose provider calls all threw is retried before the
 * run aborts loudly. A single transient failure (HTTP 429, brief disconnect)
 * must not kill a 33k-symbol run, but a hard-down provider must not spin
 * forever either — after the retries the run throws into `phase: 'error'`.
 */
const MAX_ERRORED_BATCH_RETRIES = 2;

/**
 * Fraction of (provider-succeeded) symbols that may fall back to a generic
 * signature-derived summary before we emit a degradation warning. Above this
 * the provider is almost certainly returning HTTP 200 with no usable text —
 * typically a "thinking" model burning the output budget on reasoning tokens.
 */
const DEGRADATION_WARN_FRACTION = 0.5;

export class SummarizationPipeline {
  constructor(
    private store: Store,
    private inferenceService: InferenceService,
    private rootPath: string,
    private config: SummarizationConfig,
    private progress?: ProgressState,
    /** When provided, stale embeddings are invalidated on summary rewrite so
     *  the next EmbeddingPipeline run picks them up. */
    private vectorStore?: VectorStore | null,
  ) {}

  /**
   * Per-run tally of symbols where the provider returned a successful
   * (non-error) response but produced no usable summary, so we stored a generic
   * signature-derived one instead. Reset at the start of each
   * `summarizeUnsummarized` run. Used for silent-degradation detection.
   */
  private silentFallbacks = 0;
  /** Per-run count of symbols whose provider call did not throw. */
  private providerSuccesses = 0;

  async summarizeUnsummarized(signal?: AbortSignal): Promise<number> {
    let totalSummarized = 0;
    let batch: ReturnType<Store['getUnsummarizedSymbols']>;

    // Reset run-scoped degradation counters.
    this.silentFallbacks = 0;
    this.providerSuccesses = 0;

    const total = this.store.countUnsummarizedSymbols(this.config.kinds);
    if (total === 0) return 0;

    this.progress?.update('summarization', {
      phase: 'running',
      processed: 0,
      total,
      startedAt: Date.now(),
      completedAt: 0,
    });

    /**
     * Ids that produced a provider *response* this run (storable or
     * silent-empty alike). Empty responses are deliberately NOT stored (they
     * retry on a later run once the model is fixed), so they stay
     * `summary IS NULL` and the next fetch would re-select them — without
     * this set, partial-progress batches re-bill those rows once per
     * subsequent batch (GH#1423). Ids whose call *threw* are deliberately
     * excluded: a transient 429 must be retried, not skipped.
     */
    const attempted = new Set<number>();
    /** Consecutive zero-progress batches whose calls all threw (retry budget). */
    let erroredBatchRetries = 0;

    try {
      // Intentionally `for (;;)` with explicit breaks rather than
      // `while (batch.length === batchSize)`: an errored tail batch (shorter
      // than batchSize) must still re-fetch to retry its failed symbols.
      for (;;) {
        // Cooperative cancellation: bail out at batch boundaries instead of
        // running to completion when the owning project was already disposed.
        if (signal?.aborted) {
          throw signal.reason instanceof Error ? signal.reason : new Error('Summarization aborted');
        }

        batch = this.store.getUnsummarizedSymbols(this.config.kinds, this.config.batchSize);
        if (batch.length === 0) break;

        // Skip rows already answered this run — re-sending them only burns
        // provider budget for a response we already know is unusable.
        const fresh = batch.filter((sym) => !attempted.has(sym.id));
        if (fresh.length === 0) {
          // Only reachable as a tail: every remaining unsummarized row was
          // already billed-empty this run (a zero-progress batch breaks
          // below before the next fetch). Nothing left to bill.
          logger.debug('Summarization: remaining rows already attempted this run — stopping');
          break;
        }

        const { results, errored } = await this.summarizeBatch(fresh, signal);
        for (const { id, summary } of results) {
          this.store.updateSymbolSummary(id, summary);
          // Summary feeds buildEmbeddingText — drop any stale vector so the
          // next indexUnembedded cycle re-embeds this symbol.
          this.vectorStore?.delete(id);
          totalSummarized++;
        }
        const erroredIds = new Set(errored);
        for (const sym of fresh) {
          if (!erroredIds.has(sym.id)) attempted.add(sym.id);
        }

        this.progress?.update('summarization', { processed: totalSummarized });
        logger.debug(
          { batch: batch.length, total: totalSummarized },
          'Summarization batch complete',
        );

        if (results.length > 0) {
          erroredBatchRetries = 0;
        } else if (errored.length === 0) {
          // Nothing in this batch was storable, yet the provider answered
          // every call: every row is still `summary IS NULL`, so the next
          // fetch would return this exact batch — the loop that re-billed
          // one reporter's account ~261k times (GH#1423). Stop: the leftovers
          // stay unsummarized and retry on a later run, and the degradation
          // warning below can finally fire (it was unreachable while the
          // loop never exited).
          logger.warn(
            { batchSize: fresh.length },
            'Summarization batch produced no stored summaries — stopping this run ' +
              'instead of re-billing the same symbols',
          );
          break;
        } else if (erroredBatchRetries < MAX_ERRORED_BATCH_RETRIES) {
          // The provider *threw* instead of answering — a different failure
          // from the silent-empty case above (typically a transient 429 or a
          // brief disconnect). The errored ids were kept out of `attempted`,
          // so the next fetch retries exactly them.
          erroredBatchRetries++;
          logger.warn(
            {
              batchSize: fresh.length,
              errorCount: errored.length,
              attempt: erroredBatchRetries,
              maxAttempts: MAX_ERRORED_BATCH_RETRIES + 1,
            },
            'Summarization batch hit provider errors — retrying the failed symbols',
          );
        } else {
          // Retry budget exhausted. A run that stored nothing at all faces a
          // dead-from-the-start provider: abort loudly into `phase: 'error'`
          // (the outer catch) rather than spinning or misreporting
          // `completed` at zero progress. But a run that DID progress (e.g.
          // one poison-pill symbol with a prompt the provider always rejects
          // among thousands of healthy ones) must not be rewritten as a
          // failure: keep the progress with a loud warn and leave the
          // leftovers `summary IS NULL` for the next run.
          if (totalSummarized === 0) {
            throw new Error(
              `Summarization batch failed: ${errored.length} of ${fresh.length} provider ` +
                `calls errored after ${MAX_ERRORED_BATCH_RETRIES + 1} attempts`,
            );
          }
          logger.warn(
            {
              errorCount: errored.length,
              summarized: totalSummarized,
              attempts: MAX_ERRORED_BATCH_RETRIES + 1,
            },
            'Summarization stopping: some symbols errored persistently — kept progress, ' +
              'leftovers stay unsummarized and retry on the next run',
          );
          break;
        }

        // A short fetch means the queue is drained — unless errored ids are
        // still pending retry (kept out of `attempted`, so the next fetch
        // returns exactly them).
        if (batch.length < this.config.batchSize && errored.length === 0) break;
      }

      this.maybeWarnSilentDegradation();

      this.progress?.update('summarization', {
        phase: 'completed',
        processed: totalSummarized,
        completedAt: Date.now(),
      });
    } catch (e) {
      this.progress?.update('summarization', {
        phase: 'error',
        error: e instanceof Error ? e.message : String(e),
      });
      throw e;
    }

    return totalSummarized;
  }

  private async summarizeBatch(
    symbols: ReturnType<Store['getUnsummarizedSymbols']>,
    signal?: AbortSignal,
  ): Promise<{ results: { id: number; summary: string }[]; errored: number[] }> {
    const results: { id: number; summary: string }[] = [];
    /** Ids whose provider call threw (vs. answered with unusable text). */
    const errored: number[] = [];
    const template = PROMPTS.summarize_symbol;
    const concurrency = this.config.concurrency;

    // Default true preserves the original behavior (summarize from full source).
    const fromDocstrings = this.config.summarizeFromDocstrings !== false;

    const summarizeOne = async (sym: (typeof symbols)[number]): Promise<void> => {
      // Per-symbol abort check — stop scheduling fresh fetches once the owner
      // signalled cancellation.
      if (signal?.aborted) return;
      try {
        let source = this.readSource(sym.file_path, sym.byte_start, sym.byte_end) ?? '';
        // IPI opt-out: drop leading docstrings/comment blocks (the highest-risk
        // injection surface) so only signature + structural body reaches the LLM.
        if (!fromDocstrings && source) {
          source = stripLeadingDocstrings(source);
        }

        const prompt = template.build({
          kind: sym.kind,
          name: sym.name,
          fqn: sym.fqn ?? '',
          signature: sym.signature ?? '',
          source,
        });

        const summary = await this.inferenceService.generate(prompt, {
          maxTokens: this.config.maxTokens ?? template.maxTokens,
          temperature: template.temperature,
          signal,
        });

        // The provider call returned without throwing — count it as a success
        // for degradation-rate math regardless of whether the body was usable.
        this.providerSuccesses++;

        // Sanitize the model output before it is stored / surfaced into an
        // agent context (the input was untrusted code).
        const cleaned = sanitizeGeneratedSummary(summary);
        if (cleaned) {
          results.push({ id: sym.id, summary: cleaned });
          return;
        }

        // HTTP 200 but no usable summary — typically a thinking model that spent
        // the output budget on reasoning. Record the silent degradation for the
        // warning, but do NOT store a placeholder: leaving the symbol
        // unsummarized means it is retried on the next run once the model is
        // fixed, instead of being masked by a low-value signature restatement
        // that search can already derive from the stored signature.
        this.silentFallbacks++;
      } catch (e) {
        logger.warn({ symbolId: sym.id, name: sym.name, error: e }, 'Failed to summarize symbol');
        errored.push(sym.id);
      }
    };

    if (concurrency <= 1) {
      for (const sym of symbols) {
        if (signal?.aborted) break;
        await summarizeOne(sym);
      }
    } else {
      for (let i = 0; i < symbols.length; i += concurrency) {
        if (signal?.aborted) break;
        const chunk = symbols.slice(i, i + concurrency);
        await Promise.all(chunk.map(summarizeOne));
      }
    }

    return { results, errored };
  }

  /**
   * Emit ONE warning per run when the share of provider-succeeded symbols that
   * fell back to a generic summary exceeds DEGRADATION_WARN_FRACTION. This
   * surfaces "HTTP 200 with empty body" silent degradation — naming the likely
   * cause (a thinking model consuming the output budget) and the remedy.
   */
  private maybeWarnSilentDegradation(): void {
    if (this.providerSuccesses === 0) return;
    const fraction = this.silentFallbacks / this.providerSuccesses;
    if (fraction <= DEGRADATION_WARN_FRACTION) return;

    logger.warn(
      {
        silentFallbacks: this.silentFallbacks,
        providerSuccesses: this.providerSuccesses,
        fallbackFraction: Number(fraction.toFixed(2)),
        threshold: DEGRADATION_WARN_FRACTION,
      },
      'AI summarizer returned successful responses with no usable summary for most symbols — ' +
        'likely a "thinking" model consuming the entire output budget on reasoning tokens. ' +
        'Remedy: raise ai.summarize_max_tokens, or set ai.openaiExtraBody to disable thinking ' +
        '(e.g. { "reasoning_effort": "none" } / { "chat_template_kwargs": { "enable_thinking": false } }).',
    );
  }

  private readSource(filePath: string, byteStart: number, byteEnd: number): string | null {
    try {
      const absPath = path.resolve(this.rootPath, filePath);
      const content = fs.readFileSync(absPath, 'utf-8');
      const slice = content.slice(byteStart, byteEnd);
      const lines = slice.split('\n');
      if (lines.length > MAX_SOURCE_LINES) {
        return `${lines.slice(0, MAX_SOURCE_LINES).join('\n')}\n// ... truncated`;
      }
      return slice;
    } catch {
      return null;
    }
  }
}
